import { Alert, Box, Typography } from '@mui/material';
import { AsyncJobStatus } from '@opengov/stories-shared/types';
import React, { useEffect, useState } from 'react';

import {
  EBannerVariant,
  EProcessStatus,
  IExportPdfProcess,
  IFolderCopyProcess,
  IPublicationViewMessages,
  IStoriesCopyProcess,
  IStoryListMessages
} from '../../../../types';
import {
  dismissExportPdfBannerMutation,
  dismissExportPublicationPdfBannerMutation,
  dismissStoriesCopyProcessBannerMutation,
  dismissStoryFoldersCopyProcessesBannerMutation
} from '../../../graphQL/queries';
import { postQuery } from '../../../utils/helper';
import { removeOrUpdateLocalStorageValue } from '../../../utils/localStorageUtils';
import { styles } from './StoryListBanner.styles';

export interface IStoryListBannerProps {
  isAdmin: boolean;
  messages: IStoryListMessages;
  folderCopyProcess?: IFolderCopyProcess;
  storiesCopyProcesses?: IStoriesCopyProcess[];
  exportPdfProcess?: IExportPdfProcess;
  isPublicationViewBanner?: boolean;
  isPublicationSectionExportBannerVisible?: boolean;
  publicationMessages?: IPublicationViewMessages;
  bulkChangeStatusProcessStatus?: string;
  bulkChangeStatusFailureBanner?: boolean;
  publicationId?: string;
}

type BannerParams = {
  variant: EBannerVariant;
  header: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  children?: React.ReactNode;
};

export const getBulkStatusChangeParams = (
  bulkChangeStatusProcessStatus: string,
  publicationMessages: IPublicationViewMessages,
  handleDismiss?: () => void
): BannerParams | null => {
  switch (bulkChangeStatusProcessStatus) {
    case AsyncJobStatus.Enqueued:
    case AsyncJobStatus.InProgress:
      return {
        variant: EBannerVariant.INFO,
        header: publicationMessages.storyStatusUpdating,
        children: <Typography variant="body2">{publicationMessages.storyStatusUpdatingDescription}</Typography>
      };
    case AsyncJobStatus.Error:
      return {
        variant: EBannerVariant.ERROR,
        header: publicationMessages.storyStatusUpdateError,
        dismissible: true,
        onDismiss: handleDismiss,
        children: <Typography variant="body2">{publicationMessages.storyStatusUpdateErrorDescription}</Typography>
      };
    default:
      return null;
  }
};

export const getFolderCopyProcessBannerParams = (
  process: IFolderCopyProcess,
  messages: IStoryListMessages,
  isPublicationViewBanner: boolean,
  publicationMessages: IPublicationViewMessages,
  handleDismiss: () => void
): BannerParams | null => {
  const { status, message } = process;
  switch (status) {
    case EProcessStatus.InProgress:
      return {
        variant: EBannerVariant.INFO,
        header: isPublicationViewBanner ? publicationMessages.copyingSection : messages.copyingFolder,
        children: (
          <Typography variant="body2">
            {message || (isPublicationViewBanner ? publicationMessages.creatingNewSectionProcess : messages.creatingNewFolderProcess)}
          </Typography>
        )
      };
    case EProcessStatus.Success:
      return {
        variant: EBannerVariant.SUCCESS,
        header: isPublicationViewBanner ? publicationMessages.copySectionComplete : messages.copyFolderComplete,
        dismissible: true,
        onDismiss: handleDismiss,
        children: (
          <Typography variant="body2">
            {message || (isPublicationViewBanner ? publicationMessages.copySectionSuccess : messages.copyFolderSuccess)}
          </Typography>
        )
      };
    case EProcessStatus.Error:
      return {
        variant: EBannerVariant.ERROR,
        header: isPublicationViewBanner ? publicationMessages.copySectionFailed : messages.copyFolderFailed,
        dismissible: true,
        onDismiss: handleDismiss,
        children: <Typography variant="body2">{message || messages.processNotCompleted}</Typography>
      };
    default:
      return null;
  }
};

export const formatCreatedAt = (createdAt: string): string => {
  const date = new Date(createdAt);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let str = `${monthNames[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}, `;
  str += date.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
  return str;
};

export const exportPdfMessage = (
  createdAt: string,
  messages: IStoryListMessages,
  requestId: string,
  isPublication = false
) => (
  <Typography variant="body2" component="p" data-test="export-pdf-success-message">
    {messages.pdfGeneratedDescription}
    <b>{formatCreatedAt(createdAt)}</b>.&nbsp;
    <a
      target="_blank"
      href={`/api/stories/v1/export-requests/${requestId}/isPublication/${isPublication}/download-pdf`}
      rel="noopener noreferrer"
      download={true}
      aria-label="Download generated PDF"
      style={{ textDecoration: 'underline' }}
    >
      {messages.clickHere}
    </a>
    &nbsp;{messages.toStartDownload}
  </Typography>
);

export const getExportPdfBannerParams = (
  process: IExportPdfProcess,
  messages: IStoryListMessages,
  isPublicationViewBanner: boolean,
  isPublicationSectionExportBannerVisible: boolean,
  publicationMessages: IPublicationViewMessages,
  handleDismiss: () => void
): BannerParams | null => {
  const { status } = process;
  switch (status) {
    case EProcessStatus.InProgress:
      return {
        variant: EBannerVariant.INFO,
        header: messages.exportingPdf,
        dismissible: true,
        onDismiss: handleDismiss,
        children: (
          <Typography variant="body2">
            {isPublicationViewBanner ? publicationMessages.exportingPdfProcessDescription : messages.exportingPdfProcessDescription}
          </Typography>
        )
      };
    case EProcessStatus.Success: {
      const isPublicationExport = !isPublicationSectionExportBannerVisible && isPublicationViewBanner;
      return {
        variant: EBannerVariant.SUCCESS,
        header: messages.pdfGenerated,
        dismissible: true,
        onDismiss: handleDismiss,
        children: exportPdfMessage(process.createdAt, messages, process.id, isPublicationExport)
      };
    }
    case EProcessStatus.Error:
      return {
        variant: EBannerVariant.ERROR,
        header: messages.pdfGenerateFailed,
        dismissible: true,
        onDismiss: handleDismiss,
        children: (
          <Typography variant="body2">
            {isPublicationViewBanner ? publicationMessages.pdfGenerateFailedDescrption : messages.pdfGenerateFailedDescrption}
          </Typography>
        )
      };
    default:
      return null;
  }
};

export const getStoriesCopyProcessBannerParams = (
  process: IStoriesCopyProcess,
  messages: IStoryListMessages,
  handleDismiss: () => void
): BannerParams | null => {
  const { status, message } = process;
  switch (status) {
    case EProcessStatus.InProgress:
      return {
        variant: EBannerVariant.INFO,
        header: messages.copyingStories,
        children: <Typography variant="body2">{message || messages.copyingStoriesProcess}</Typography>
      };
    case EProcessStatus.Success:
      return {
        variant: EBannerVariant.SUCCESS,
        header: messages.copyStoriesComplete,
        dismissible: true,
        onDismiss: handleDismiss,
        children: <Typography variant="body2">{message || messages.copyStoriesSuccess}</Typography>
      };
    case EProcessStatus.Error:
      return {
        variant: EBannerVariant.ERROR,
        header: messages.copyStoriesFailed,
        dismissible: true,
        onDismiss: handleDismiss,
        children: <Typography variant="body2">{message || messages.processNotCompleted}</Typography>
      };
    default:
      return null;
  }
};

export const StoryListBanner: React.FC<IStoryListBannerProps> = (props) => {
  const {
    isAdmin,
    messages,
    folderCopyProcess,
    storiesCopyProcesses,
    exportPdfProcess,
    isPublicationViewBanner = false,
    publicationMessages,
    isPublicationSectionExportBannerVisible = false,
    bulkChangeStatusProcessStatus,
    bulkChangeStatusFailureBanner = false,
    publicationId
  } = props;
  const [bannerParams, setBannerParams] = useState<BannerParams | null>(null);

  useEffect(() => {
    setBannerParams(null);

    const latestStoriesCopyProcess = storiesCopyProcesses && storiesCopyProcesses[0];
    if (latestStoriesCopyProcess && !latestStoriesCopyProcess.suppressDisplayBanner) {
      const handleDismiss = () => {
        setBannerParams(null);
        void postQuery({
          query: dismissStoriesCopyProcessBannerMutation,
          variables: { processId: latestStoriesCopyProcess._id }
        });
      };
      const params = getStoriesCopyProcessBannerParams(latestStoriesCopyProcess, messages, handleDismiss);

      if (folderCopyProcess && !folderCopyProcess.suppressDisplayBanner) {
        void postQuery({
          query: dismissStoryFoldersCopyProcessesBannerMutation,
          variables: { folderId: folderCopyProcess.destinationFolderId }
        });
      }

      if (exportPdfProcess && latestStoriesCopyProcess.status === EProcessStatus.InProgress && !exportPdfProcess.suppressDisplayBanner) {
        void postQuery({
          query: dismissExportPdfBannerMutation,
          variables: { id: exportPdfProcess.id }
        });
      }
      setBannerParams({ ...params, dismissible: isAdmin && params.dismissible });
    } else if (folderCopyProcess && !folderCopyProcess.suppressDisplayBanner) {
      const handleDismiss = () => {
        setBannerParams(null);
        void postQuery({
          query: dismissStoryFoldersCopyProcessesBannerMutation,
          variables: { folderId: folderCopyProcess.destinationFolderId }
        });
      };
      const params = getFolderCopyProcessBannerParams(folderCopyProcess, messages, isPublicationViewBanner, publicationMessages, handleDismiss);
      setBannerParams({ ...params, dismissible: isAdmin && params.dismissible });
    }
    if (exportPdfProcess && !exportPdfProcess.suppressDisplayBanner) {
      if (latestStoriesCopyProcess && latestStoriesCopyProcess.status === EProcessStatus.InProgress) return;

      const handleDismiss = () => {
        setBannerParams(null);
        void postQuery({
          query: isPublicationViewBanner && !isPublicationSectionExportBannerVisible
            ? dismissExportPublicationPdfBannerMutation
            : dismissExportPdfBannerMutation,
          variables: { id: exportPdfProcess.id }
        });
      };
      const params = getExportPdfBannerParams(exportPdfProcess, messages, isPublicationViewBanner, isPublicationSectionExportBannerVisible, publicationMessages, handleDismiss);
      setBannerParams({ ...params });
    }
    if ((bulkChangeStatusProcessStatus || bulkChangeStatusFailureBanner) && publicationMessages) {
      let params: BannerParams | null = null;
      if (bulkChangeStatusProcessStatus === AsyncJobStatus.InProgress || bulkChangeStatusProcessStatus === AsyncJobStatus.Enqueued) {
        params = getBulkStatusChangeParams(bulkChangeStatusProcessStatus, publicationMessages);
      } else if (bulkChangeStatusProcessStatus === AsyncJobStatus.Error || bulkChangeStatusFailureBanner) {
        const handleDismiss = () => {
          setBannerParams(null);
          removeOrUpdateLocalStorageValue(publicationId, 'bulkChangeStatusFailureBanner');
        };
        params = getBulkStatusChangeParams(AsyncJobStatus.Error, publicationMessages, handleDismiss);
      }
      if (params) {
        setBannerParams({ ...params });
      }
    }
  }, [exportPdfProcess, folderCopyProcess, storiesCopyProcesses, isAdmin, messages, bulkChangeStatusProcessStatus]);

  if (!bannerParams) return null;

  const variantClass = { info: 'variantInfo', success: 'variantSuccess', error: 'variantError', warning: 'variantWarning' }[bannerParams.variant] ?? '';

  return (
    <Alert
      severity={bannerParams.variant}
      onClose={bannerParams.dismissible ? bannerParams.onDismiss : undefined}
      sx={isPublicationViewBanner ? {} : styles.banner}
      data-test="story-list-banner"
      className={variantClass}
      slotProps={{ closeButton: { 'data-test': 'story-list-banner-dismiss-button' } as object }}
    >
      <Typography variant="body2" fontWeight={600} data-test="story-list-banner-header">{bannerParams.header}</Typography>
      <Box data-test="story-list-banner-body">{bannerParams.children}</Box>
    </Alert>
  );
};
